import * as Linking from 'expo-linking';

const PRIMARY_APP_SCHEME = 'kingdombiblequiz';
const LEGACY_APP_SCHEME = 'kingdomquiz';
const WEB_HOST = 'https://kingdombiblequiz.com';

// Type of supported deep link parameters
export interface InviteLinkParams {
  type: 'room' | 'event' | 'referral' | 'quiz' | string;
  id?: string; // roomId or eventId
  code?: string; // joinCode or referralCode
  referrer?: string; // referrer user id
  sessionId?: string; // quiz session id
  token?: string; // quiz access token
  lang?: string; // locale for quiz
}

/**
 * Returns the initial URL that opened the app (if any)
 */
export async function getInitialURL(): Promise<string | null> {
  try {
    return await Linking.getInitialURL();
  } catch (e) {
    if (__DEV__) console.warn('[inviteLink] Failed to get initial URL', e);
    return null;
  }
}

/**
 * Add a listener to handle future deep links while the app is running.
 * Returns a function to unsubscribe.
 */
export function addLinkingListener(callback: (url: string) => void): () => void {
  const listener = ({ url }: { url: string }) => callback(url);
  const subscription = Linking.addEventListener('url', listener);
  return () => subscription.remove();
}

/**
 * Parse an incoming URL into structured parameters understood by the app.
 * Supported formats:
 *  kingdombiblequiz://room/<roomId>?code=<joinCode>
 *  kingdomquiz://room/<roomId>?code=<joinCode> (legacy)
 *  kingdombiblequiz://event/<eventId>?code=<joinCode>
 *  kingdombiblequiz://referral/<referrerId>?code=<referralCode>
 *  kingdombiblequiz://join?room=<roomId>&code=<joinCode> (legacy)
 *  https://www.kingdombiblequiz.com/join/room/<roomId>?code=<joinCode>
 *  https://www.kingdombiblequiz.com/app/quiz/<sessionId>?token=<token>&lang=<lang>
 */
export async function parseInviteLink(url: string): Promise<InviteLinkParams | null> {
  try {
    const parsed = Linking.parse(url);
    const path = parsed.path ?? '';
    const segments = path.split('/').filter(Boolean);
    const queryParams = parsed.queryParams ?? {};
    const roomIdFromQuery = typeof queryParams.room === 'string' ? queryParams.room : undefined;
    const joinCodeFromQuery = typeof queryParams.code === 'string' ? queryParams.code : undefined;

    if (!segments.length) {
      if (roomIdFromQuery) {
        return {
          type: 'room',
          id: decodeURIComponent(roomIdFromQuery),
          code: joinCodeFromQuery,
        };
      }
      return null;
    }

    // Handle hosted PWA quiz links like /app/quiz/:sessionId
    if (segments[0] === 'app' && segments[1] === 'quiz') {
      const sessionId = segments[2] ?? '';
      if (!sessionId) {
        return null;
      }

      return {
        type: 'quiz',
        sessionId: decodeURIComponent(sessionId),
        token: typeof queryParams.token === 'string' ? queryParams.token : undefined,
        lang: typeof queryParams.lang === 'string' ? queryParams.lang : undefined,
      };
    }

    if (segments[0] === 'quiz') {
      const sessionId = segments[1] ?? '';
      if (!sessionId) {
        return null;
      }

      return {
        type: 'quiz',
        sessionId: decodeURIComponent(sessionId),
        token: typeof queryParams.token === 'string' ? queryParams.token : undefined,
        lang: typeof queryParams.lang === 'string' ? queryParams.lang : undefined,
      };
    }

    if (segments[0] === 'join' && segments[1] === 'room' && segments[2]) {
      return {
        type: 'room',
        id: decodeURIComponent(segments[2]),
        code: joinCodeFromQuery,
      };
    }

    if (segments[0] === 'join' && segments[1]) {
      return {
        type: 'room',
        id: decodeURIComponent(segments[1]),
        code: joinCodeFromQuery,
      };
    }

    switch (segments[0]) {
      case 'room':
        return { type: 'room', id: decodeURIComponent(segments[1] ?? ''), code: joinCodeFromQuery };
      case 'event':
        return { type: 'event', id: decodeURIComponent(segments[1] ?? ''), code: joinCodeFromQuery };
      case 'referral':
        return { type: 'referral', referrer: decodeURIComponent(segments[1] ?? ''), code: joinCodeFromQuery };
      default:
        return null;
    }
  } catch (e) {
    if (__DEV__) console.warn('[inviteLink] Failed to parse link', e);
    return null;
  }
}

/**
 * Create a room invitation link
 */
export function createRoomInviteLink(roomId: string, joinCode: string): string {
  if (!roomId || !joinCode) {
    throw new Error('Room ID and join code are required for room invite links');
  }
  return `${WEB_HOST}/join/room/${encodeURIComponent(roomId)}?code=${encodeURIComponent(joinCode)}`;
}

export function createRoomInviteAppLink(roomId: string, joinCode: string): string {
  if (!roomId || !joinCode) {
    throw new Error('Room ID and join code are required for room invite links');
  }
  return `${PRIMARY_APP_SCHEME}://room/${encodeURIComponent(roomId)}?code=${encodeURIComponent(joinCode)}`;
}

/**
 * Create an event invitation link
 */
export function createEventInviteLink(eventId: string, joinCode: string): string {
  if (!eventId || !joinCode) {
    throw new Error('Event ID and join code are required for event invite links');
  }
  return `${PRIMARY_APP_SCHEME}://event/${encodeURIComponent(eventId)}?code=${encodeURIComponent(joinCode)}`;
}

/**
 * Create a referral link
 */
export function createReferralLink(referrerId: string, referralCode: string): string {
  if (!referrerId || !referralCode) {
    throw new Error('Referrer ID and referral code are required for referral links');
  }
  return `${LEGACY_APP_SCHEME}://referral/${encodeURIComponent(referrerId)}?code=${encodeURIComponent(referralCode)}`;
}
